import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip, CircularProgress, Alert, TablePagination, Breadcrumbs, Button,
    TextField, InputAdornment, FormControl, Select, MenuItem
} from '@mui/material';
import { Refresh, Fastfood, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllFoodBookings } from '../../services/Api';

const GetAllBookings = () => {
    const navigate = useNavigate();

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 1000);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchBookings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllFoodBookings(page + 1, rowsPerPage, debouncedSearch, statusFilter);

            if (response) {
                const src = response;
                let targetArray = [];

                if (Array.isArray(src)) targetArray = src;
                else if (src && typeof src === 'object') {
                    if (Array.isArray(src.bookings)) targetArray = src.bookings;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                    else if (Array.isArray(src.result)) targetArray = src.result;
                }

                setBookings(targetArray);
                setTotalRows(src.totalItems || src.totalRecords || src.totalCount || src.total || targetArray.length || 0);
            } else {
                setError('Failed to fetch bookings');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch, statusFilter]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed' || s === 'delivered' || s === 'packed') return { bgcolor: 'success.light', color: 'success.dark' };
        if (s === 'cancelled' || s === 'rejected') return { bgcolor: 'error.light', color: 'error.dark' };
        if (s === 'pending' || s === 'processing' || s === 'preparing') return { bgcolor: 'warning.light', color: 'warning.dark' };
        if (s === 'ready_for_pickup' || s === 'out_for_delivery') return { bgcolor: 'info.light', color: 'info.dark' };
        return { bgcolor: 'grey.200', color: 'grey.800' };
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Typography color="text.primary">Bookings</Typography>
                    <Typography color="text.primary">Food Bookings</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Fastfood sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                                Food Bookings
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage all food delivery orders efficiently
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                                displayEmpty
                                sx={{ borderRadius: '20px', bgcolor: '#ffffff', '& fieldset': { borderColor: '#e0e0e0' } }}
                            >
                                <MenuItem value="" disabled>Booking Status</MenuItem>
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="preparing">Preparing</MenuItem>
                                <MenuItem value="packed">Packed</MenuItem>
                                <MenuItem value="ready_for_pickup">Ready for Pickup</MenuItem>
                                <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
                                <MenuItem value="delivered">Delivered</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            placeholder="Search..."
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                width: 180,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '20px',
                                    backgroundColor: '#ffffff',
                                    '& fieldset': { borderColor: '#e0e0e0' },
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <IconButton
                            onClick={fetchBookings}
                            disabled={loading}
                            sx={{
                                color: 'primary.main',
                                border: '1px solid',
                                borderColor: '#e0e0e0',
                                borderRadius: '50%',
                                p: 1
                            }}
                        >
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: '#e0e0e0', borderRadius: '12px', overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e0e0e0' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Booking </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : bookings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No bookings found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                bookings.map((booking, index) => (
                                    <TableRow key={booking.id || booking._id || `row-${index}`} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{booking.bookingNumber || booking.bookingId || `#${booking._id || booking.id}`}</TableCell>
                                        <TableCell>{booking.name || '-'}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>₹{booking.amount || '0'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={(booking.status || 'Pending').replace(/_/g, ' ')}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    ...getStatusColor(booking.status)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{booking.createdAt || booking.date ? new Date(booking.createdAt || booking.date).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => navigate(`/admin/bookings/details/${booking.id || booking._id}`)}
                                                sx={{
                                                    borderRadius: '20px',
                                                    textTransform: 'none',
                                                    color: 'primary.main',
                                                    borderColor: 'primary.light',
                                                    fontSize: '0.75rem',
                                                    px: 2
                                                }}
                                            >
                                                View Details
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default GetAllBookings;
